import type { AthleteProfile, FtpPolicySelection } from "./types/athleteProfile";

export type RideSurfaceType = "road" | "gravel" | "unknown";

export type FtpSelectionSource = "athlete-profile-default" | "ride-override";

export type FtpSelectionResult = {
  ftpWatts: number;
  rideSurfaceType: RideSurfaceType;
  source: FtpSelectionSource;
  sourceLabel: string;
  selectedPolicy: FtpPolicySelection;
  rideOverridePolicy: FtpPolicySelection | null;
  effectiveDateIso: string | null;
  policyResolutionPath: string[];
  unknownDefaultSurface: "road";
};

export type HistoricalFtpSelectionResult = FtpSelectionResult & {
  historicalPolicy: "historical_snapshot" | "ftp_history" | "profile_fallback";
  fallbackUsed: boolean;
};

const UNKNOWN_RIDE_DEFAULT_SURFACE: "road" = "road";

export function createFtpPolicySelection(kind: FtpPolicySelection["kind"], manualFtpWatts: number | null = null): FtpPolicySelection {
  return { kind, manualFtpWatts };
}

function normalizeManualFtpWatts(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.round(value) : null;
}

function formatPolicyLabel(policy: FtpPolicySelection): string {
  switch (policy.kind) {
    case "athlete-profile-default":
      return "default analysis FTP";
    case "indoor":
      return "indoor FTP";
    case "outdoor":
      return "outdoor FTP";
    case "road":
      return "road FTP";
    case "gravel":
      return "gravel FTP";
    case "manual":
      return "manual FTP";
  }
}

function formatSourceLabel(source: FtpSelectionSource, policy: FtpPolicySelection): string {
  const prefix = source === "ride-override" ? "Ride override" : "Athlete profile";
  return `${prefix} — ${formatPolicyLabel(policy)}`;
}

function resolvePolicyValue(profile: AthleteProfile, policy: FtpPolicySelection): { ftpWatts: number; effectiveDateIso: string | null; resolutionPath: string[] } {
  const fallbackFtp = profile.performance.ftpWatts;
  const fallbackDate = profile.performance.ftpEffectiveDateIso;

  switch (policy.kind) {
    case "athlete-profile-default":
      return {
        ftpWatts: fallbackFtp,
        effectiveDateIso: fallbackDate,
        resolutionPath: ["default analysis policy -> current FTP"]
      };
    case "indoor":
      return {
        ftpWatts: profile.performance.indoorFtpWatts ?? fallbackFtp,
        effectiveDateIso: fallbackDate,
        resolutionPath: [profile.performance.indoorFtpWatts ? "indoor FTP selected" : "indoor FTP missing, fell back to current FTP"]
      };
    case "outdoor":
      return {
        ftpWatts: profile.performance.outdoorFtpWatts ?? fallbackFtp,
        effectiveDateIso: fallbackDate,
        resolutionPath: [profile.performance.outdoorFtpWatts ? "outdoor FTP selected" : "outdoor FTP missing, fell back to current FTP"]
      };
    case "road":
      return {
        ftpWatts: profile.performance.roadFtpWatts ?? fallbackFtp,
        effectiveDateIso: fallbackDate,
        resolutionPath: [profile.performance.roadFtpWatts ? "road FTP selected" : "road FTP missing, fell back to current FTP"]
      };
    case "gravel":
      return {
        ftpWatts: profile.performance.gravelFtpWatts ?? fallbackFtp,
        effectiveDateIso: fallbackDate,
        resolutionPath: [profile.performance.gravelFtpWatts ? "gravel FTP selected" : "gravel FTP missing, fell back to current FTP"]
      };
    case "manual": {
      const manualFtpWatts = normalizeManualFtpWatts(policy.manualFtpWatts);
      return {
        ftpWatts: manualFtpWatts ?? fallbackFtp,
        effectiveDateIso: manualFtpWatts ? new Date().toISOString() : fallbackDate,
        resolutionPath: [manualFtpWatts ? `manual FTP selected: ${manualFtpWatts} W` : "manual FTP missing, fell back to current FTP"]
      };
    }
  }

  return {
    ftpWatts: fallbackFtp,
    effectiveDateIso: fallbackDate,
    resolutionPath: ["unknown policy, fell back to current FTP"]
  };
}

function parseIso(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function selectFtpForHistoricalRide(
  profile: AthleteProfile,
  rideSurfaceType: RideSurfaceType,
  rideDateIso: string,
  rideOverridePolicy: FtpPolicySelection | number | null = null
): HistoricalFtpSelectionResult {
  const base = selectFtpForRide(profile, rideSurfaceType, rideOverridePolicy);
  if (rideOverridePolicy) {
    return {
      ...base,
      historicalPolicy: "historical_snapshot",
      fallbackUsed: false,
      policyResolutionPath: [...base.policyResolutionPath, "historical ride override applied"]
    };
  }

  const rideTs = parseIso(rideDateIso);
  const history = [...profile.performance.ftpHistory]
    .filter(entry => Number.isFinite(entry.ftpWatts) && entry.ftpWatts > 0)
    .sort((left, right) => (parseIso(right.effectiveDateIso) ?? 0) - (parseIso(left.effectiveDateIso) ?? 0));

  if (rideTs !== null) {
    const snapshot = history.find(entry => {
      const effectiveTs = parseIso(entry.effectiveDateIso);
      return effectiveTs !== null && effectiveTs <= rideTs;
    });
    if (snapshot) {
      return {
        ...base,
        ftpWatts: snapshot.ftpWatts,
        sourceLabel: `Historical FTP snapshot — ${snapshot.source}`,
        effectiveDateIso: snapshot.effectiveDateIso,
        historicalPolicy: "historical_snapshot",
        fallbackUsed: false,
        policyResolutionPath: [
          ...base.policyResolutionPath,
          `historical snapshot selected (${snapshot.ftpWatts} W @ ${snapshot.effectiveDateIso})`
        ]
      };
    }
  }

  if (history.length > 0) {
    const nearest = history[0];
    return {
      ...base,
      ftpWatts: nearest.ftpWatts,
      sourceLabel: `FTP history fallback — ${nearest.source}`,
      effectiveDateIso: nearest.effectiveDateIso,
      historicalPolicy: "ftp_history",
      fallbackUsed: true,
      policyResolutionPath: [
        ...base.policyResolutionPath,
        "no snapshot matched ride date",
        `fallback to latest ftp history (${nearest.ftpWatts} W @ ${nearest.effectiveDateIso})`
      ]
    };
  }

  return {
    ...base,
    historicalPolicy: "profile_fallback",
    fallbackUsed: true,
    policyResolutionPath: [
      ...base.policyResolutionPath,
      "no historical ftp found, fell back to current profile ftp"
    ]
  };
}

export function resolveFtpSelection(profile: AthleteProfile, selectedPolicy: FtpPolicySelection): FtpSelectionResult {
  const resolved = resolvePolicyValue(profile, selectedPolicy);

  return {
    ftpWatts: resolved.ftpWatts,
    rideSurfaceType: "unknown",
    source: "athlete-profile-default",
    sourceLabel: formatSourceLabel("athlete-profile-default", selectedPolicy),
    selectedPolicy,
    rideOverridePolicy: null,
    effectiveDateIso: resolved.effectiveDateIso,
    policyResolutionPath: resolved.resolutionPath,
    unknownDefaultSurface: UNKNOWN_RIDE_DEFAULT_SURFACE
  };
}

export function inferRideSurfaceType(fileName: string): RideSurfaceType {
  const normalized = fileName.toLowerCase();

  if (normalized.includes("gravel") || normalized.includes("unbound")) {
    return "gravel";
  }

  if (normalized.includes("road") || normalized.includes("crit") || normalized.includes("tt")) {
    return "road";
  }

  return "unknown";
}

export function selectFtpForRide(
  profile: AthleteProfile,
  rideSurfaceType: RideSurfaceType,
  rideOverridePolicy: FtpPolicySelection | number | null = null
): FtpSelectionResult {
  const normalizedRideOverridePolicy = typeof rideOverridePolicy === "number"
    ? createFtpPolicySelection("manual", rideOverridePolicy)
    : rideOverridePolicy;
  const selectedPolicy = normalizedRideOverridePolicy ?? profile.performance.defaultAnalysisFtpPolicy;
  const resolved = resolvePolicyValue(profile, selectedPolicy);
  const source: FtpSelectionSource = normalizedRideOverridePolicy ? "ride-override" : "athlete-profile-default";

  return {
    ftpWatts: resolved.ftpWatts,
    rideSurfaceType,
    source,
    sourceLabel: formatSourceLabel(source, selectedPolicy),
    selectedPolicy,
    rideOverridePolicy: normalizedRideOverridePolicy,
    effectiveDateIso: resolved.effectiveDateIso,
    policyResolutionPath: [
      ...resolved.resolutionPath,
      normalizedRideOverridePolicy ? "ride override took precedence" : "using athlete profile default policy"
    ],
    unknownDefaultSurface: UNKNOWN_RIDE_DEFAULT_SURFACE
  };
}
